/**
 * Sarathi-X — Customer Portal (Phone OTP + My Trips + Account)
 *
 * Single-page UX with three states driven by auth + first_login_completed:
 *   1. Logged out      → Phone + OTP entry
 *   2. First login     → Welcome modal (name + WhatsApp opt-in)
 *   3. Logged in       → Trips + Account tabs
 *
 * Feature-flagged on backend (CUSTOMER_FEATURE_ENABLED). When disabled,
 * the page shows a "Coming soon" notice and the OTP request 503's.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Phone, ArrowRight, ArrowLeft, Calendar, LogOut, Download, Trash2, Shield, MessageSquare, Check, X, User, Mail, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import TripDetailsModal from "@/components/TripDetailsModal";
import TurnstileWidget from "@/components/TurnstileWidget";
import { useAppConfig } from "@/hooks/useAppConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/customer`;
const ACCESS_KEY = "sarathix_customer_access";
const REFRESH_KEY = "sarathix_customer_refresh";
// FIX-8 — store auth tokens in sessionStorage so they're cleared when the
// tab closes. localStorage survives reboots and is unnecessarily long-lived
// for a single browsing session.
const STORAGE = sessionStorage;

function authHeader() {
  const t = STORAGE.getItem(ACCESS_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function refreshSession() {
  const r = STORAGE.getItem(REFRESH_KEY);
  if (!r) return false;
  try {
    const res = await axios.post(`${API}/refresh`, { refresh_token: r });
    STORAGE.setItem(ACCESS_KEY, res.data.access_token);
    STORAGE.setItem(REFRESH_KEY, res.data.refresh_token);
    return true;
  } catch {
    STORAGE.removeItem(ACCESS_KEY);
    STORAGE.removeItem(REFRESH_KEY);
    return false;
  }
}

const STATUS_COLORS = {
  PENDING: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  PENDING_REVIEW: "bg-red-500/15 text-red-400 border-red-500/30",
  CONFIRMED: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  IN_PROGRESS: "bg-green-500/15 text-green-400 border-green-500/30",
  COMPLETED: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  DECLINED: "bg-red-900/15 text-red-300 border-red-900/30",
  CANCELLED: "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

const CANCELLATION_REASONS = [
  "Booked by mistake",
  "Change of plans",
  "Flight cancelled or rescheduled",
  "Current time slot doesn't work for me",
  "Found alternative transport",
];

const CANCELLABLE_STATUSES = ["PENDING", "PENDING_REVIEW", "CONFIRMED"];

export default function CustomerPortal() {
  const [featureEnabled, setFeatureEnabled] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  // Login state
  const [phone, setPhone] = useState("");
  const [otpRequested, setOtpRequested] = useState(false);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState("");
  // Turnstile (Cloudflare bot challenge) — only gates the OTP request step.
  const appConfig = useAppConfig();
  const configLoaded = !!appConfig;
  const turnstileEnabled = configLoaded && appConfig?.turnstile?.enabled;
  const turnstileSiteKey = configLoaded ? appConfig?.turnstile?.site_key : null;
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  // Welcome modal
  const [welcomeName, setWelcomeName] = useState("");
  const [welcomeWA, setWelcomeWA] = useState(true);

  // Tabs + data
  const [tab, setTab] = useState("trips");
  const [bookings, setBookings] = useState([]);
  const [showLogoutAll, setShowLogoutAll] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  // Profile edit state
  const [profileName, setProfileName] = useState("");
  const [profileEmail, setProfileEmail] = useState("");
  const [profileWA, setProfileWA] = useState(true);
  const [profileDirty, setProfileDirty] = useState(false);

  // Cancel-ride modal state
  const [cancelTarget, setCancelTarget] = useState(null);  // booking object being cancelled
  const [cancelReason, setCancelReason] = useState("");
  const [cancelSuccess, setCancelSuccess] = useState(null);  // ack payload from API

  // Trip details modal — opened when a user taps a trip card
  const [detailsTarget, setDetailsTarget] = useState(null);

  // Format a stored phone (e.g. "919876543210") into a readable "+91 98765 43210".
  const formatPhone = (raw) => {
    if (!raw) return "";
    const digits = String(raw).replace(/\D/g, "");
    if (digits.length === 12 && digits.startsWith("91")) {
      return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
    }
    if (digits.length === 10) {
      return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
    }
    return `+${digits}`;
  };

  const loadMe = useCallback(async () => {
    setLoading(true);
    try {
      const featRes = await axios.get(`${API}/feature-status`);
      setFeatureEnabled(featRes.data.enabled);
    } catch {
      setFeatureEnabled(false);
    }
    if (!STORAGE.getItem(ACCESS_KEY)) {
      setMe(null);
      setLoading(false);
      return;
    }
    try {
      const r = await axios.get(`${API}/me`, { headers: authHeader() });
      setMe(r.data);
    } catch (err) {
      if (err.response?.status === 401) {
        const ok = await refreshSession();
        if (ok) {
          const r2 = await axios.get(`${API}/me`, { headers: authHeader() });
          setMe(r2.data);
        } else {
          setMe(null);
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  // Load bookings whenever logged in
  useEffect(() => {
    if (!me) return;
    axios.get(`${API}/bookings`, { headers: authHeader() })
      .then((r) => setBookings(r.data.bookings || []))
      .catch(() => setBookings([]));
    // Sync editable profile form fields with `me`
    setProfileName(me.name || "");
    setProfileEmail(me.email || "");
    setProfileWA(me.whatsapp_opt_in !== false);
    setProfileDirty(false);
  }, [me]);

  const handleProfileSave = async () => {
    setBusy("profile-save");
    try {
      const r = await axios.put(`${API}/profile`,
        { name: profileName.trim() || null, email: profileEmail.trim() || "", whatsapp_opt_in: profileWA },
        { headers: authHeader() });
      setMe(r.data);
      setProfileDirty(false);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save profile");
    } finally { setBusy(""); }
  };

  const handleRequestOTP = async () => {
    const clean = phone.replace(/\D/g, "");
    if (clean.length < 10) { toast.error("Enter a valid 10-digit mobile"); return; }
    if (turnstileEnabled && !turnstileToken) {
      toast.error("Please complete the captcha verification first.");
      return;
    }
    setBusy("request");
    try {
      await axios.post(`${API}/otp/request`, { phone: clean, turnstile_token: turnstileToken });
      toast.success("OTP sent. Check your SMS.");
      setOtpRequested(true);
    } catch (err) {
      // Reset Turnstile so the next attempt gets a fresh token.
      setTurnstileToken(null);
      if (turnstileRef.current) turnstileRef.current.reset();
      if (err.response?.status === 503) {
        toast.error("Customer accounts are coming soon. We're in private beta.");
      } else {
        toast.error(err.response?.data?.detail || "Couldn't send OTP");
      }
    } finally { setBusy(""); }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) { toast.error("Enter the 6-digit OTP"); return; }
    setBusy("verify");
    try {
      const clean = phone.replace(/\D/g, "");
      const r = await axios.post(`${API}/otp/verify`, { phone: clean, otp });
      STORAGE.setItem(ACCESS_KEY, r.data.access_token);
      STORAGE.setItem(REFRESH_KEY, r.data.refresh_token);
      setMe(r.data.customer);
      setOtp("");
      setOtpRequested(false);
      setPhone("");
      toast.success("Signed in");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally { setBusy(""); }
  };

  const handleCompleteWelcome = async () => {
    if (!welcomeName.trim()) { toast.error("Please enter your name"); return; }
    setBusy("welcome");
    try {
      const r = await axios.post(`${API}/complete-welcome`,
        { name: welcomeName.trim(), whatsapp_opt_in: welcomeWA },
        { headers: authHeader() });
      setMe(r.data);
      toast.success(`Welcome, ${r.data.name}!`);
    } catch {
      toast.error("Couldn't save profile");
    } finally { setBusy(""); }
  };

  const handleLogout = async () => {
    const r = STORAGE.getItem(REFRESH_KEY);
    if (r) {
      try { await axios.post(`${API}/logout`, { refresh_token: r }); } catch (_e) { /* idempotent */ }
    }
    STORAGE.removeItem(ACCESS_KEY);
    STORAGE.removeItem(REFRESH_KEY);
    setMe(null);
    toast.success("Signed out");
  };

  const handleLogoutAll = async () => {
    try {
      const r = await axios.post(`${API}/logout-all`, {}, { headers: authHeader() });
      toast.success(`Signed out of ${r.data.sessions_revoked} device(s)`);
    } catch { toast.error("Couldn't log out"); }
    STORAGE.removeItem(ACCESS_KEY);
    STORAGE.removeItem(REFRESH_KEY);
    setMe(null);
    setShowLogoutAll(false);
  };

  const openCancelModal = (booking) => {
    setCancelTarget(booking);
    setCancelReason("");
    setCancelSuccess(null);
  };

  const closeCancelModal = () => {
    setCancelTarget(null);
    setCancelReason("");
    setCancelSuccess(null);
  };

  const handleConfirmCancel = async () => {
    if (!cancelTarget || !cancelReason) {
      toast.error("Please select a reason for cancellation");
      return;
    }
    setBusy("cancel");
    try {
      const res = await axios.post(
        `${API}/bookings/${cancelTarget.booking_reference}/cancel`,
        { reason: cancelReason },
        { headers: authHeader() }
      );
      setCancelSuccess(res.data);
      // Optimistic UI update — flip the trip card to CANCELLED locally
      setBookings((prev) => prev.map((b) =>
        b.booking_reference === cancelTarget.booking_reference
          ? { ...b, status: "CANCELLED", cancellation_reason: cancelReason }
          : b
      ));
      // Close details modal if it was open behind the cancel modal
      setDetailsTarget(null);
      toast.success("Your ride has been cancelled");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't cancel ride. Please try again.");
    } finally { setBusy(""); }
  };

  const handleDataExport = async () => {
    setBusy("export");
    try {
      const r = await axios.get(`${API}/data-export`, { headers: authHeader() });
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sarathix-data-${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data has been downloaded");
    } catch { toast.error("Export failed"); }
    finally { setBusy(""); }
  };

  const handleDelete = async () => {
    if (deleteConfirmText !== "DELETE") { toast.error('Type "DELETE" to confirm'); return; }
    setBusy("delete");
    try {
      const r = await axios.delete(`${API}/account`, { headers: authHeader() });
      toast.success(`Account deleted. ${r.data.bookings_anonymized} booking(s) anonymized.`);
      STORAGE.removeItem(ACCESS_KEY);
      STORAGE.removeItem(REFRESH_KEY);
      setMe(null);
      setShowDelete(false);
    } catch { toast.error("Deletion failed"); }
    finally { setBusy(""); }
  };

  if (loading) {
    return <div className="min-h-screen bg-sarathi-background flex items-center justify-center text-sarathi-text-muted text-sm">Loading...</div>;
  }

  // ── Feature OFF ─────────────────────────────────────────────────────
  if (featureEnabled === false) {
    return (
      <div className="min-h-screen bg-sarathi-background flex items-center justify-center px-4">
        <div className="max-w-md text-center" data-testid="customer-portal-disabled">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-semibold mb-6">
            <Shield className="w-3 h-3" /> COMING SOON
          </div>
          <h1 className="font-outfit text-3xl font-bold text-white mb-3">My Trips is in private beta</h1>
          <p className="text-sarathi-text-secondary text-sm leading-relaxed">
            We're putting the final polish on phone-OTP sign-in. Book your ride
            without an account in the meantime — your trip is tracked by your
            booking reference.
          </p>
          <a href="/" className="inline-flex items-center gap-2 mt-6 text-sarathi-primary hover:underline text-sm">
            <ArrowRight className="w-4 h-4 rotate-180" /> Back to home
          </a>
        </div>
      </div>
    );
  }

  // ── Logged out ───────────────────────────────────────────────────────
  if (!me) {
    return (
      <div className="min-h-screen bg-sarathi-background flex items-center justify-center px-4">
        <div className="w-full max-w-sm bg-sarathi-surface border border-white/10 rounded-2xl p-8" data-testid="customer-login">
          <div className="text-center mb-6">
            <div className="inline-flex w-12 h-12 rounded-full bg-sarathi-primary/15 items-center justify-center mb-3">
              <Phone className="w-5 h-5 text-sarathi-primary" />
            </div>
            <h1 className="font-outfit text-2xl font-bold text-white">Sign in</h1>
            <p className="text-sarathi-text-muted text-sm mt-1">Track your Sarathi-X trips</p>
          </div>
          {!otpRequested ? (
            <>
              <label htmlFor="phone" className="text-sarathi-text-secondary text-xs uppercase tracking-wider font-semibold block mb-2">Mobile Number</label>
              <div className="flex gap-2 mb-4">
                <span className="inline-flex items-center px-3 bg-sarathi-background border border-white/10 rounded-lg text-sarathi-text-secondary text-sm">+91</span>
                <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="" inputMode="numeric"
                  maxLength={10}
                  className="flex-1 bg-sarathi-background border-white/10 text-white rounded-lg h-10 text-sm" data-testid="phone-input" />
              </div>
              {turnstileEnabled && turnstileSiteKey ? (
                <div className="flex justify-center mb-4" data-testid="turnstile-container">
                  <TurnstileWidget
                    siteKey={turnstileSiteKey}
                    theme="dark"
                    size="flexible"
                    onToken={setTurnstileToken}
                    widgetRef={turnstileRef}
                  />
                </div>
              ) : null}
              <Button onClick={handleRequestOTP} disabled={busy === "request" || !configLoaded || (turnstileEnabled && !turnstileToken)} className="w-full bg-sarathi-primary hover:bg-sarathi-primary-hover text-white h-10" data-testid="request-otp-btn">
                {busy === "request" ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send OTP <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
            </>
          ) : (
            <>
              <p className="text-sarathi-text-muted text-xs mb-3">OTP sent to +91 {phone.replace(/(\d{6})(\d{4})/, "XXXXXX$2")}</p>
              <label htmlFor="otp" className="text-sarathi-text-secondary text-xs uppercase tracking-wider font-semibold block mb-2">6-digit OTP</label>
              <Input id="otp" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0,6))} placeholder="" inputMode="numeric"
                maxLength={6}
                className="mb-4 bg-sarathi-background border-white/10 text-white rounded-lg h-10 text-center text-lg tracking-widest font-mono" data-testid="otp-input" />
              <Button onClick={handleVerifyOTP} disabled={busy === "verify" || otp.length !== 6} className="w-full bg-sarathi-primary hover:bg-sarathi-primary-hover text-white h-10 mb-2" data-testid="verify-otp-btn">
                {busy === "verify" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & Sign In"}
              </Button>
              <button type="button" onClick={() => { setOtpRequested(false); setOtp(""); }}
                className="w-full text-sarathi-text-muted text-xs hover:text-white transition-colors">
                Change number
              </button>
            </>
          )}
          <p className="text-sarathi-text-muted text-[11px] text-center mt-6 leading-relaxed">
            By signing in, you agree to our <a href="/terms" className="underline">Terms</a> and <a href="/privacy" className="underline">Privacy Policy</a>.
          </p>
        </div>
      </div>
    );
  }

  // ── First-login welcome ──────────────────────────────────────────────
  if (!me.first_login_completed) {
    return (
      <div className="min-h-screen bg-sarathi-background flex items-center justify-center px-4">
        <div className="w-full max-w-sm bg-sarathi-surface border border-white/10 rounded-2xl p-8" data-testid="welcome-modal">
          <h1 className="font-outfit text-2xl font-bold text-white mb-1">Welcome to Sarathi-X 👋</h1>
          <p className="text-sarathi-text-muted text-sm mb-6">Tell us your name so we can personalise your trips.</p>
          <label htmlFor="welcomeName" className="text-sarathi-text-secondary text-xs uppercase tracking-wider font-semibold block mb-2">Name</label>
          <Input id="welcomeName" value={welcomeName} onChange={(e) => setWelcomeName(e.target.value)} placeholder="Your name"
            className="mb-4 bg-sarathi-background border-white/10 text-white rounded-lg h-10" data-testid="welcome-name" />
          <label className="flex items-center gap-2 mb-6 text-sarathi-text-secondary text-sm cursor-pointer">
            <input type="checkbox" checked={welcomeWA} onChange={(e) => setWelcomeWA(e.target.checked)}
              className="w-4 h-4 accent-sarathi-primary cursor-pointer" data-testid="welcome-wa-optin" />
            <span>Send me ride updates on WhatsApp</span>
          </label>
          <Button onClick={handleCompleteWelcome} disabled={busy === "welcome" || !welcomeName.trim()}
            className="w-full bg-sarathi-primary hover:bg-sarathi-primary-hover text-white h-10" data-testid="welcome-submit">
            {busy === "welcome" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue"}
          </Button>
          {bookings.length > 0 && (
            <p className="text-emerald-400 text-xs text-center mt-4">
              ✓ Found {bookings.length} past trip{bookings.length === 1 ? "" : "s"} on this number — they're now in your account.
            </p>
          )}
        </div>
      </div>
    );
  }

  // ── Signed in: Trips + Account ───────────────────────────────────────
  return (
    <div className="min-h-screen bg-sarathi-background px-4 py-8" data-testid="customer-portal">
      <div className="max-w-3xl mx-auto">
        <a
          href="/"
          className="inline-flex items-center gap-1.5 text-sarathi-text-muted hover:text-white text-sm mb-4 transition-colors"
          data-testid="back-to-home-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </a>
        <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="font-outfit text-2xl font-bold text-white">Hi, {me.name || "there"}</h1>
            <p className="text-sarathi-text-muted text-sm" data-testid="customer-phone">{formatPhone(me.phone) || me.phone_masked}</p>
          </div>
          <Button onClick={handleLogout} variant="outline" size="sm"
            className="border-white/10 text-sarathi-text-secondary hover:text-white" data-testid="logout-btn">
            <LogOut className="w-3.5 h-3.5 mr-1.5" /> Sign out
          </Button>
        </div>

        <div className="flex gap-1 mb-5 bg-sarathi-surface border border-white/10 rounded-xl p-1 w-fit overflow-x-auto">
          {[
            { id: "trips", label: "My Trips", icon: Calendar },
            { id: "profile", label: "Profile", icon: User },
            { id: "account", label: "Account", icon: Shield },
          ].map((t) => (
            <button
              type="button"
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
                tab === t.id ? "bg-sarathi-primary text-white" : "text-sarathi-text-secondary hover:text-white"
              }`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "trips" && (
          <div data-testid="trips-tab">
            <p className="text-sarathi-text-muted text-xs mb-3">
              {bookings.length > 0
                ? `Showing ${bookings.length} trip${bookings.length === 1 ? "" : "s"}`
                : "Ready when you are."}
            </p>
            {bookings.length === 0 ? (
              <div className="text-center py-16 bg-sarathi-surface border border-white/10 rounded-xl">
                <Calendar className="w-10 h-10 text-sarathi-text-muted mx-auto mb-3 opacity-40" />
                <p className="text-sarathi-text-secondary text-sm">No trips yet.</p>
                <a href="/" className="inline-block mt-3 text-sarathi-primary text-sm hover:underline">Book your first ride →</a>
              </div>
            ) : (
              <div className="space-y-2.5">
                {bookings.map((b) => {
                  const fare = b.fare_data;
                  const total = fare?.totalFare || fare?.total || fare?.displayTotal;
                  const status = (b.status || "PENDING").toUpperCase();
                  return (
                    <button
                      key={b.booking_reference}
                      type="button"
                      onClick={() => setDetailsTarget(b)}
                      className="w-full text-left bg-sarathi-surface border border-white/10 rounded-xl p-4 hover:border-sarathi-primary/30 hover:bg-sarathi-surface/80 transition-colors focus:outline-none focus:ring-2 focus:ring-sarathi-primary/40"
                      data-testid={`trip-${b.booking_reference}`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sarathi-primary font-mono text-xs">{b.booking_reference}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_COLORS[status] || STATUS_COLORS.PENDING}`}>{status}</span>
                        </div>
                        {total != null && (
                          <span className="text-sarathi-primary font-outfit font-bold text-sm">
                            {typeof total === "number" ? `₹${total.toLocaleString("en-IN")}` : total}
                          </span>
                        )}
                      </div>
                      <p className="text-white text-sm truncate">{b.pickup_location} → {b.drop_location}</p>
                      <p className="text-sarathi-text-muted text-xs mt-1">{b.date} · {b.time} · {b.ride_type}</p>
                      {status === "CANCELLED" && b.cancellation_reason && (
                        <p className="text-slate-400 text-xs mt-2 italic" data-testid={`trip-cancel-reason-${b.booking_reference}`}>
                          Cancelled: {b.cancellation_reason}
                        </p>
                      )}
                      <p className="text-sarathi-text-muted text-[11px] mt-2">Tap for details →</p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === "profile" && (
          <div className="space-y-3" data-testid="profile-tab">
            <div className="bg-sarathi-surface border border-white/10 rounded-xl p-5">
              <h3 className="text-white text-sm font-semibold mb-1">Your information</h3>
              <p className="text-sarathi-text-muted text-xs mb-5">Keep this up to date so we can reach you about your rides.</p>

              <label htmlFor="profile-name" className="text-sarathi-text-secondary text-xs uppercase tracking-wider font-semibold flex items-center gap-1.5 mb-2">
                <User className="w-3 h-3" /> Name
              </label>
              <Input id="profile-name" value={profileName}
                onChange={(e) => { setProfileName(e.target.value); setProfileDirty(true); }}
                placeholder="Your full name"
                className="mb-4 bg-sarathi-background border-white/10 text-white rounded-lg h-10"
                data-testid="profile-name-input" />

              <label htmlFor="profile-mobile" className="text-sarathi-text-secondary text-xs uppercase tracking-wider font-semibold flex items-center gap-1.5 mb-2">
                <Phone className="w-3 h-3" /> Mobile Number
              </label>
              <Input id="profile-mobile" value={formatPhone(me.phone)}
                disabled readOnly
                className="mb-1 bg-sarathi-background/50 border-white/5 text-sarathi-text-muted rounded-lg h-10 cursor-not-allowed"
                data-testid="profile-mobile-input" />
              <p className="text-sarathi-text-muted text-[11px] mb-4">
                Your mobile is locked to your account identity and can't be changed.
              </p>

              <label htmlFor="profile-email" className="text-sarathi-text-secondary text-xs uppercase tracking-wider font-semibold flex items-center gap-1.5 mb-2">
                <Mail className="w-3 h-3" /> Email
              </label>
              <Input id="profile-email" type="email" value={profileEmail}
                onChange={(e) => { setProfileEmail(e.target.value); setProfileDirty(true); }}
                placeholder="you@example.com (optional)"
                className="mb-4 bg-sarathi-background border-white/10 text-white rounded-lg h-10"
                data-testid="profile-email-input" />

              <label className="flex items-center gap-2 mb-5 text-sarathi-text-secondary text-sm cursor-pointer select-none">
                <input type="checkbox" checked={profileWA}
                  onChange={(e) => { setProfileWA(e.target.checked); setProfileDirty(true); }}
                  className="w-4 h-4 accent-sarathi-primary cursor-pointer"
                  data-testid="profile-wa-checkbox" />
                <MessageSquare className="w-3.5 h-3.5 text-green-400" />
                Send me ride updates on WhatsApp
              </label>

              <Button onClick={handleProfileSave} disabled={busy === "profile-save" || !profileDirty}
                className="bg-sarathi-primary hover:bg-sarathi-primary-hover text-white h-10 disabled:opacity-40"
                data-testid="profile-save-btn">
                {busy === "profile-save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save Changes</>}
              </Button>
            </div>
          </div>
        )}

        {tab === "account" && (
          <div className="space-y-3" data-testid="account-tab">
            <div className="bg-sarathi-surface border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="w-4 h-4 text-green-400" />
                <h3 className="text-white text-sm font-semibold">WhatsApp Updates</h3>
              </div>
              <p className="text-sarathi-text-muted text-xs">
                You will {me.whatsapp_opt_in ? "" : "not "}receive booking confirmations and ride updates on WhatsApp.
              </p>
            </div>

            <div className="bg-sarathi-surface border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Download className="w-4 h-4 text-blue-400" />
                <h3 className="text-white text-sm font-semibold">Download my data</h3>
              </div>
              <p className="text-sarathi-text-muted text-xs mb-3">
                Per the DPDP Act 2023, you can download a copy of all personal data we hold about you.
              </p>
              <Button onClick={handleDataExport} disabled={busy === "export"} size="sm"
                className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white" data-testid="data-export-btn">
                {busy === "export" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Download JSON"}
              </Button>
            </div>

            <div className="bg-sarathi-surface border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <LogOut className="w-4 h-4 text-amber-400" />
                <h3 className="text-white text-sm font-semibold">Sign out of all devices</h3>
              </div>
              <p className="text-sarathi-text-muted text-xs mb-3">
                Revoke every active session on every browser and app.
              </p>
              <Button onClick={() => setShowLogoutAll(true)} variant="outline" size="sm"
                className="h-8 text-xs border-amber-500/30 text-amber-400 hover:bg-amber-500/10" data-testid="logout-all-btn">
                Sign out everywhere
              </Button>
            </div>

            <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Trash2 className="w-4 h-4 text-red-400" />
                <h3 className="text-white text-sm font-semibold">Delete my account</h3>
              </div>
              <p className="text-sarathi-text-muted text-xs mb-3 leading-relaxed">
                This permanently deletes your profile. Past booking records are
                anonymised but retained for 7 years (CGST Act 2017 + Income Tax Act 1961).
                This cannot be undone.
              </p>
              <Button onClick={() => setShowDelete(true)} variant="outline" size="sm"
                className="h-8 text-xs border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="delete-account-btn">
                <Trash2 className="w-3 h-3 mr-1.5" /> Delete account
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Logout-all confirmation modal */}
      <Dialog open={showLogoutAll} onOpenChange={setShowLogoutAll}>
        <DialogContent data-testid="logout-all-modal" className="bg-sarathi-surface border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Sign out of all devices?</DialogTitle>
            <DialogDescription className="text-sarathi-text-secondary">
              You'll need to sign in again on every device. This is useful if you've lost a phone or used a public computer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button onClick={() => setShowLogoutAll(false)} variant="outline" className="border-white/10 text-sarathi-text-secondary"><X className="w-4 h-4 mr-1.5" /> Cancel</Button>
            <Button onClick={handleLogoutAll} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="logout-all-confirm"><Check className="w-4 h-4 mr-1.5" /> Yes, sign out everywhere</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation modal */}
      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent data-testid="delete-modal" className="bg-sarathi-surface border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Delete account permanently?</DialogTitle>
            <DialogDescription className="text-sarathi-text-secondary leading-relaxed">
              Your profile and contact details will be deleted. Past booking records will be anonymised but retained for 7 years as required by Indian tax law (CGST Act 2017 + Income Tax Act 1961). After 7 years, all data is permanently purged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sarathi-text-secondary text-sm">Type <span className="text-red-400 font-mono font-bold">DELETE</span> to confirm:</p>
            <Input value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE" className="bg-sarathi-background border-white/10 text-white" data-testid="delete-confirm-input" />
          </div>
          <DialogFooter className="gap-2">
            <Button onClick={() => { setShowDelete(false); setDeleteConfirmText(""); }} variant="outline" className="border-white/10 text-sarathi-text-secondary">Cancel</Button>
            <Button onClick={handleDelete} disabled={deleteConfirmText !== "DELETE" || busy === "delete"}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50" data-testid="delete-confirm-btn">
              {busy === "delete" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4 mr-1.5" /> Delete forever</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Trip details modal — opened from My Trips list */}
      <TripDetailsModal
        booking={detailsTarget}
        open={!!detailsTarget}
        onClose={() => setDetailsTarget(null)}
        onCancelClick={(b) => openCancelModal(b)}
      />

      {/* Cancel ride modal — reason picker + ack */}
      <Dialog open={!!cancelTarget} onOpenChange={(open) => { if (!open) closeCancelModal(); }}>
        <DialogContent data-testid="cancel-ride-modal" className="bg-sarathi-surface border-white/10">
          {cancelSuccess ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-400" /> Cancellation received
                </DialogTitle>
                <DialogDescription className="text-sarathi-text-secondary leading-relaxed">
                  {cancelSuccess.message}
                </DialogDescription>
              </DialogHeader>
              <div className="bg-sarathi-background/50 border border-white/5 rounded-lg p-3 text-xs space-y-1">
                <p className="text-sarathi-text-muted">Booking <span className="font-mono text-sarathi-primary">{cancelSuccess.booking_reference}</span></p>
                <p className="text-sarathi-text-muted">Reason: <span className="text-white">{cancelSuccess.cancellation_reason}</span></p>
                {cancelSuccess.hours_before_pickup != null && (
                  <p className="text-sarathi-text-muted">Cancelled <span className="text-white">{cancelSuccess.hours_before_pickup.toFixed(1)}h</span> before pickup</p>
                )}
              </div>
              <DialogFooter>
                <Button onClick={closeCancelModal} className="bg-sarathi-primary hover:bg-sarathi-primary-hover text-white" data-testid="cancel-ride-close-btn">
                  Got it
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-white">Cancel your ride?</DialogTitle>
                <DialogDescription className="text-sarathi-text-secondary leading-relaxed">
                  Booking <span className="font-mono text-sarathi-primary">{cancelTarget?.booking_reference}</span> · {cancelTarget?.date} · {cancelTarget?.time}
                  <br />
                  Please tell us why so we can improve. Our team will then reach out to confirm the cancellation per our policy.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2" data-testid="cancel-reasons-list">
                {CANCELLATION_REASONS.map((reason) => (
                  <label
                    key={reason}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      cancelReason === reason
                        ? "border-sarathi-primary bg-sarathi-primary/10"
                        : "border-white/10 hover:border-white/20 bg-sarathi-background/30"
                    }`}
                  >
                    <input
                      type="radio"
                      name="cancel-reason"
                      value={reason}
                      checked={cancelReason === reason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      className="accent-sarathi-primary"
                      data-testid={`cancel-reason-${reason.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                    />
                    <span className="text-sm text-white">{reason}</span>
                  </label>
                ))}
              </div>
              <DialogFooter className="gap-2">
                <Button onClick={closeCancelModal} variant="outline" className="border-white/10 text-sarathi-text-secondary" data-testid="cancel-ride-dismiss-btn">
                  Keep ride
                </Button>
                <Button
                  onClick={handleConfirmCancel}
                  disabled={!cancelReason || busy === "cancel"}
                  className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                  data-testid="cancel-ride-confirm-btn"
                >
                  {busy === "cancel" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cancel ride"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
